import { I18n } from "@assets/strings";
import moment from "moment";
import React from "react";
import styled from "styled-components";
import checkIcon from "@assets/images/check.svg";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Group } from "@domain/group/group";
import { Disbursement } from "@domain/wallet/wallet";
import { DisbursementType } from "@domain/wallet/walletDraft";
import { useModalTools } from "@ui/modals/modalContext";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { Primary } from "@ui/components/containers/primary";
import { ShareroomCashout } from "@ui/components/group/shareroom/dashboard/shareroomCashout";
interface DisbursementTableProps extends React.HTMLAttributes<HTMLDivElement> {
	disbursementList: Disbursement[] | null;
	shareroom: Group;
}

export const DisbursementTable: React.FC<DisbursementTableProps> = ({ disbursementList, shareroom }) => {
	const { createModal } = useModalTools();
	const currentProfile = useCurrentProfile();

	const { trackingService } = useServices();
	return (
		<>
			{disbursementList ? (
				<StyledTable>
					<tr>
						<StyledTableHeader>{I18n.t("shareroom.dashboard.transactions.table.price.title")}</StyledTableHeader>
						<StyledTableHeader>{I18n.t("shareroom.dashboard.transactions.table.periodicity.title")}</StyledTableHeader>
						<StyledTableHeader>{I18n.t("shareroom.dashboard.transactions.table.balance.title")}</StyledTableHeader>
						<StyledTableHeader>{I18n.t("shareroom.dashboard.transactions.table.action.title")}</StyledTableHeader>
					</tr>
					{disbursementList.map((item) => (
						<tr key={item.month}>
							<td>
								<StyledAmount>
									{item.unitPrice} {currentProfile?.currency}
								</StyledAmount>
							</td>
							<td>
								<StyledPeriodicity>
									{moment(item.startDate).format("LL")}{" "}
									{I18n.t("shareroom.dashboard.transactions.table.periodicity.description")}{" "}
									{moment(item.endDate).format("LL")}
								</StyledPeriodicity>
							</td>
							<td>
								<StyledBalance>
									{item.cashableIUC} {currentProfile?.currency}
								</StyledBalance>
							</td>
							<td>
								{item.status === DisbursementType.WITHDRAWN ? (
									<StyledContainerWithdrawn>
										<StyledTextRetired>
											{I18n.t("shareroom.dashboard.transactions.table.withdrawn_button.label")}
										</StyledTextRetired>
										<StyledIcon>
											<img src={checkIcon} />
										</StyledIcon>
									</StyledContainerWithdrawn>
								) : (
									<StyledButtonAction
										disabled={item.status !== DisbursementType.WITHDRAWABLE}
										onClick={() => {
											trackingService.track("Disbursement / teacher");
											createModal((close) => (
												<ShareroomCashout
													shareroom={shareroom}
													cashableIUC={item.cashableIUC}
													month={item.month ?? null}
													close={close}
												/>
											));
										}}
									>
										<span>{I18n.t("shareroom.dashboard.transactions.table.withdraw_button.label")}</span>
									</StyledButtonAction>
								)}
							</td>
						</tr>
					))}
				</StyledTable>
			) : null}
		</>
	);
};

const StyledTable = styled.table`
	width: 100%;
	border-spacing: 39px;
	@media (max-width: 900px) {
		table-layout: fixed;
		td {
			display: block;
			margin: 20px auto 20px;
			padding-top: 0;
		}
	}
`;
const StyledTableHeader = styled.th`
	text-align: left;
	font-weight: 600;
	font-size: 16px;
	line-height: 26px;
	border-bottom: 1px solid #ddd;
`;

const StyledAmount = styled(Primary)`
	font-weight: 700;
	font-size: 14px;
	line-height: 26px;
	color: #206af8;
`;
const StyledPeriodicity = styled(Primary)`
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;
`;
const StyledBalance = styled(Primary)`
	font-size: 14px;
	line-height: 26px;
	/* identical to box height, or 186% */

	color: rgba(52, 200, 15, 0.8);
`;
const StyledButtonAction = styled(LoadingButton)`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 5px 18px;
	gap: 10px;
	font-size: 12px;
	line-height: 16px;
	background: rgba(32, 106, 248, 0.2);
	color: #206af8;
	:hover {
		background-color: rgba(32, 106, 248, 0.2);
	}
	/* width: 54%; */
`;

const StyledContainerWithdrawn = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
`;
const StyledTextRetired = styled.div`
	font-weight: 500;
	font-size: 14px;
	line-height: 18px;
	color: rgba(1, 1, 1, 0.5);
`;
const StyledIcon = styled.div`
	margin-left: 10px;
`;
